import { Box, Typography } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { format as formatDate } from 'date-fns'
import i18n from 'i18n-js'
import { useLayoutEffect, useState } from 'react'
import { CrudsBtn, Loader, SnackBar } from '../../../../components'
import medicalStaff from '../../../../services/medicalStaff'
import {
  createObservation, getObservationList,
  getObservationTypes
} from '../../../../services/observation'
import { getPatientList } from '../../../../services/patients'
import { ListFilter } from '../../components/listFilters'
import { ObservationModal } from '../../components/modal'
import { useColumns } from './hooks/columns'
import { useStyles } from './styles'

export const ObservationList = () => {
  const { styles } = useStyles()

  const [openModal, setOpenModal] = useState(false)
  const [values, setValues] = useState({})

  const [lists, setLists] = useState({
    patient: [],
    staff: [],
    observationTypes: [],
  })

  const [isLoading, setIsLoading] = useState({ modal: false, table: false })
  const [defaultValue, setDefaultValue] = useState(null)

  const [table, setTable] = useState([])

  const [snackbar, setSnackbar] = useState({
    open: false,
    title: '',
    type: 'success',
  })

  const getObservations = async (payload) => {
    try {
      setIsLoading((prev) => ({ ...prev, table: true }))
      const response = await getObservationList(payload || values)

      setTable(
        response.data?.observations?.map((item) => ({
          id: item.id,
          comment: item.name,
          createdDate: item.created_at
            ? formatDate(
              new Date(item.created_at),
              i18n.t('general.dateTimeFormat')
            )
            : i18n.t('general.dataNotFound'),
          creator: `${item.medical_staff?.name || ''} ${item.medical_staff?.lastname || ''
            }`.trim(),
          patient: `${item.patient?.name || ''} ${item.patient?.lastname || ''
            }`.trim(),
          observation_type: item.observation_type?.name,
          patientItem: {
            id: item.patient?.id,
            label: `${item.patient?.name || ''} ${item.patient?.lastname || ''
              }`.trim(),
          },
          // por si luego necesitas el id del tipo:
          observationTypeItem: item.observation_type
            ? { id: item.observation_type?.id, label: item.observation_type?.name }
            : null,
        })) || []
      )
    } finally {
      setIsLoading((prev) => ({ ...prev, table: false }))
    }
  }

  const getObservationTypeList = async () => {
    const response = await getObservationTypes()
    const formattedTypes =
      response?.data?.observationTypes?.map((item) => ({
        id: item.id,
        label: item.name,
        value: item.type,
      })) || []

    setLists((prev) => ({
      ...prev,
      observationTypes: formattedTypes,
    }))

    // default para el modal (si no hay nada, null)
    setDefaultValue((prev) => ({
      ...prev,
      observationType: formattedTypes[0] || null,
    }))
  }

  const getPatients = async () => {
    const response = await getPatientList()
    setLists((prev) => ({
      ...prev,
      patient:
        response.data?.patients?.map((item) => ({
          id: item.id,
          label: `${item?.name || ''} ${item?.lastname || ''}`.trim(),
        })) || [],
    }))
  }

  const getStaff = async () => {
    const response = await medicalStaff.list()
    setLists((prev) => ({
      ...prev,
      staff:
        response?.map((item) => ({
          id: item.id,
          label: `${item.name || ''} ${item.lastname || ''}`.trim(),
        })) || [],
    }))
  }


  const handleChange = (item, name) => {
    const next = { ...values, [name]: item }
    setValues(next)
    getObservations(next)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setIsLoading((prev) => ({ ...prev, modal: false }))
  }

  const handleOpenModal = () => {
    setOpenModal(true)
  }

  const handleSave = async (modalValues) => {
    try {
      setIsLoading((prev) => ({ ...prev, modal: true }))


      // type id fallback: el seleccionado, o el default, o el primero, o 1
      const typeId =
        modalValues?.observationType?.id ||
        defaultValue?.observationType?.id ||
        lists?.observationTypes?.[0]?.id ||
        1

      const response = await createObservation({
        patient_id: modalValues.patient.id,
        observations: [
          {
            type: typeId,
            observation: modalValues.observation,
          },
        ],
      })

      let nextSnackbar = {
        open: true,
        title: i18n.t(
          `observation.snackbar.create.success`
        ),
        type: 'success',
      }

      if (response?.data?.success) {
        handleCloseModal()
        getObservations()
      } else {
        nextSnackbar = {
          open: true,
          title: i18n.t(
            `observation.snackbar.create.errorSend`
          ),
          type: 'warning',
        }
        setIsLoading((prev) => ({ ...prev, modal: false }))
      }

      setSnackbar(nextSnackbar)
    } catch (_) {
      setSnackbar({
        open: true,
        title: i18n.t(
          `observation.snackbar.create.errorSend`
        ),
        type: 'warning',
      })
      setIsLoading((prev) => ({ ...prev, modal: false }))
    }
  }

  const { column: columns, canCreate } = useColumns()

  useLayoutEffect(() => {
    getPatients()
    getStaff()
    getObservationTypeList()
    getObservations()
  }, [])

  return (
    <Box sx={styles.container}>
      <Typography variant="body2" color="textSecondary">
        {i18n.t('breadcrumb.observation')} / {i18n.t('breadcrumb.dashboard')}
      </Typography>

      <Box sx={styles.header}>
        <Typography variant="h5" color="primary" sx={styles.title}>
          {i18n.t('breadcrumb.observation')}
        </Typography>

        {canCreate && (
          <CrudsBtn
            text={i18n.t('cruds.observation.button')}
            size="medium"
            click={handleOpenModal}
          />
        )}
      </Box>

      <ListFilter handleChange={handleChange} values={values} lists={lists} />

      {isLoading.table && <Loader size={24} />}

      {!isLoading.table && (
        <DataGrid
          columns={columns}
          rows={table || []}
          autoHeight
          pagination
          pageSizeOptions={[10]}
          disableColumnMenu
          disableRowSelectionOnClick
          sx={styles.datagrid}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
        />
      )}

      {openModal && (
        <ObservationModal
          observationType
          handleCloseModal={handleCloseModal}
          openModal={openModal}
          lists={lists}
          onSave={handleSave}
          isLoading={isLoading.modal}
          defaultData={defaultValue}
        />
      )}

      <SnackBar
        text={snackbar.title}
        severity={snackbar.type}
        show={snackbar.open}
        HandleShow={() =>
          setSnackbar({ open: false, title: '', type: 'success' })
        }
        time={1500}
      />
    </Box>
  )
}
